/**
 * In-Browser Stress Testing Tool for Booking System
 * 
 * TEMPORARY: Remove this component before final production release
 * This component allows you to stress test the booking system
 * directly from your browser with real Firebase authentication.
 */

import React, { useState, useRef, useEffect } from 'react';
import { auth } from './firebase';
import api from './utils/api';
import './StressTestTool.css';

const StressTestTool = () => {
  // Security check - only allow specific admin email
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  useEffect(() => {
    // Only allow the admin email to access this tool
    const checkAuth = () => {
      const adminEmail = 'brightbridgetutors@gmail.com'; // Your admin email
      const currentUserEmail = auth.currentUser?.email;
      
      if (currentUserEmail === adminEmail) {
        setIsAuthorized(true);
      } else {
      }
    };
    
    if (auth.currentUser) {
      checkAuth();
    }
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        checkAuth();
      }
    });
    
    return () => unsubscribe();
  }, []);
  const [isRunning, setIsRunning] = useState(false);
  const [testConfig, setTestConfig] = useState({
    concurrentUsers: 5,
    delayMs: 100,
    testType: 'reservation',
    bookingId: '',
    duration: 5000, // 5 seconds
  });
  
  const [results, setResults] = useState({
    total: 0,
    successful: 0,
    raceConditions: 0,
    failed: 0,
    errors: [],
    log: [],
  });
  
  const abortController = useRef(null);
  const startTime = useRef(null);

  /**
   * Start stress test
   */
  const startTest = async () => {
    if (!auth.currentUser) {
      alert('Please log in first');
      return;
    }
    
    if (!testConfig.bookingId) {
      alert('Please enter a booking ID to test');
      return;
    }
    
    setIsRunning(true);
    setResults({
      total: 0,
      successful: 0,
      raceConditions: 0,
      failed: 0,
      errors: [],
      log: [],
    });
    
    abortController.current = new AbortController();
    startTime.current = Date.now();
    
    logMessage('🚀 Starting stress test...', 'info');
    logMessage(`Config: ${testConfig.concurrentUsers} concurrent users, ${testConfig.delayMs}ms delay`, 'info');
    
    try {
      switch (testConfig.testType) {
        case 'reservation':
          await runReservationTest();
          break;
        case 'confirmation':
          await runConfirmationTest();
          break;
        case 'update':
          await runUpdateTest();
          break;
        case 'mixed':
          await runMixedTest();
          break;
        default:
          break;
      }
    } catch (error) {
      logMessage(`Test error: ${error.message}`, 'error');
    } finally {
      setIsRunning(false);
      logMessage('✅ Test completed', 'success');
      displaySummary();
    }
  };

  /**
   * Stop stress test
   */
  const stopTest = () => {
    if (abortController.current) {
      abortController.current.abort();
    }
    setIsRunning(false);
    logMessage('⛔ Test stopped by user', 'warning');
  };

  /**
   * Test: Concurrent reservations
   */
  const runReservationTest = async () => {
    logMessage('Testing concurrent reservations...', 'info');
    
    const promises = [];
    const endTime = Date.now() + testConfig.duration;
    let requestCount = 0;
    
    while (Date.now() < endTime && !abortController.current.signal.aborted) {
      // Launch concurrent requests
      for (let i = 0; i < testConfig.concurrentUsers; i++) {
        promises.push(
          makeReservation(testConfig.bookingId, `user-${requestCount++}`)
        );
      }
      
      // Wait for delay
      await delay(testConfig.delayMs);
      
      // Process completed promises
      while (promises.length > 0) {
        const batch = promises.splice(0, Math.min(10, promises.length));
        const results = await Promise.allSettled(batch);
        results.forEach(result => processResult(result.value || result.reason));
      }
    }
    
    // Wait for remaining promises
    const finalResults = await Promise.allSettled(promises);
    finalResults.forEach(result => processResult(result.value || result.reason));
  };

  /**
   * Test: Concurrent confirmations
   */
  const runConfirmationTest = async () => {
    logMessage('Testing concurrent confirmations...', 'info');
    
    const promises = [];
    const endTime = Date.now() + testConfig.duration;
    let requestCount = 0;
    
    while (Date.now() < endTime && !abortController.current.signal.aborted) {
      for (let i = 0; i < testConfig.concurrentUsers; i++) {
        promises.push(
          confirmSession(testConfig.bookingId, `confirm-${requestCount++}`)
        );
      }
      
      await delay(testConfig.delayMs);
      
      // Process batch
      if (promises.length > 10) {
        const batch = promises.splice(0, 10);
        const results = await Promise.allSettled(batch);
        results.forEach(result => processResult(result.value || result.reason));
      }
    }
    
    const finalResults = await Promise.allSettled(promises);
    finalResults.forEach(result => processResult(result.value || result.reason));
  };

  /**
   * Test: Rapid updates
   */
  const runUpdateTest = async () => {
    logMessage('Testing rapid updates...', 'info');
    
    const promises = [];
    const endTime = Date.now() + testConfig.duration;
    let requestCount = 0;
    
    while (Date.now() < endTime && !abortController.current.signal.aborted) {
      // Mix updates and reservations
      for (let i = 0; i < testConfig.concurrentUsers; i++) {
        if (i % 2 === 0) {
          promises.push(
            updateBooking(testConfig.bookingId, `update-${requestCount++}`)
          );
        } else {
          promises.push(
            makeReservation(testConfig.bookingId, `reserve-${requestCount++}`)
          );
        }
      }
      
      await delay(testConfig.delayMs);
      
      // Process batch
      if (promises.length > 10) {
        const batch = promises.splice(0, 10);
        const results = await Promise.allSettled(batch);
        results.forEach(result => processResult(result.value || result.reason));
      }
    }
    
    const finalResults = await Promise.allSettled(promises);
    finalResults.forEach(result => processResult(result.value || result.reason));
  };

  /**
   * Test: Mixed operations
   */
  const runMixedTest = async () => {
    logMessage('Testing mixed operations...', 'info');
    
    const operations = ['reserve', 'confirm', 'update', 'cancel'];
    const promises = [];
    const endTime = Date.now() + testConfig.duration;
    let requestCount = 0;
    
    while (Date.now() < endTime && !abortController.current.signal.aborted) {
      for (let i = 0; i < testConfig.concurrentUsers; i++) {
        const operation = operations[Math.floor(Math.random() * operations.length)];
        
        switch (operation) {
          case 'reserve':
            promises.push(makeReservation(testConfig.bookingId, `mixed-${requestCount++}`));
            break;
          case 'confirm':
            promises.push(confirmSession(testConfig.bookingId, `mixed-${requestCount++}`));
            break;
          case 'update':
            promises.push(updateBooking(testConfig.bookingId, `mixed-${requestCount++}`));
            break;
          case 'cancel':
            promises.push(cancelBooking(testConfig.bookingId, `mixed-${requestCount++}`));
            break;
          default:
            break;
        }
      }
      
      await delay(testConfig.delayMs);
      
      // Process batch
      if (promises.length > 10) {
        const batch = promises.splice(0, 10);
        const results = await Promise.allSettled(batch);
        results.forEach(result => processResult(result.value || result.reason));
      }
    }
    
    const finalResults = await Promise.allSettled(promises);
    finalResults.forEach(result => processResult(result.value || result.reason));
  };

  /**
   * Make a reservation request - using the actual updateBooking endpoint
   */
  const makeReservation = async (bookingId, requestId) => {
    const startTime = performance.now();
    
    try {
      // Match the actual reservation format from Dashboard.js
      const response = await api.put(`/api/updateBooking/${bookingId}`, {
        email: auth.currentUser.email,
        studentName: auth.currentUser.displayName || 'Test Student',
        studentEmail: auth.currentUser.email,
        studentConfirmed: true,
        type: 'reserved',
        expectedType: 'available',
        lastKnownUpdate: new Date().toISOString(),
        requestId,
        timestamp: Date.now(),
      });
      
      const data = await response.json();
      const duration = performance.now() - startTime;
      
      return {
        success: response.ok,
        operation: 'reserve',
        requestId,
        errorCode: data.errorCode,
        message: data.message,
        duration,
      };
    } catch (error) {
      return {
        success: false,
        operation: 'reserve',
        requestId,
        error: error.message,
      };
    }
  };

  /**
   * Confirm a session - using the actual confirmAndChargeSession endpoint
   */
  const confirmSession = async (bookingId, requestId) => {
    const startTime = performance.now();
    
    try {
      const response = await api.post('/api/confirmAndChargeSession', {
        bookingId,
        requestId,
        timestamp: Date.now(),
      });
      
      const data = await response.json();
      const duration = performance.now() - startTime;
      
      return {
        success: response.ok,
        operation: 'confirm',
        requestId,
        errorCode: data.errorCode,
        message: data.message,
        duration,
      };
    } catch (error) {
      return {
        success: false,
        operation: 'confirm',
        requestId,
        error: error.message,
      };
    }
  };

  /**
   * Update a booking
   */
  const updateBooking = async (bookingId, requestId) => {
    const startTime = performance.now();
    
    try {
      const response = await api.put(`/api/updateBooking/${bookingId}`, {
        endTime: '3:30 PM',
        requestId,
        timestamp: Date.now(),
      });
      
      const data = await response.json();
      const duration = performance.now() - startTime;
      
      return {
        success: response.ok,
        operation: 'update',
        requestId,
        errorCode: data.errorCode,
        message: data.message,
        duration,
      };
    } catch (error) {
      return {
        success: false,
        operation: 'update',
        requestId,
        error: error.message,
      };
    }
  };

  /**
   * Cancel a booking - using DELETE method on updateBooking
   */
  const cancelBooking = async (bookingId, requestId) => {
    const startTime = performance.now();
    
    try {
      const response = await api.delete(`/api/deleteBooking/${bookingId}`);
      
      const data = await response.json();
      const duration = performance.now() - startTime;
      
      return {
        success: response.ok,
        operation: 'cancel',
        requestId,
        errorCode: data.errorCode,
        message: data.message,
        duration,
      };
    } catch (error) {
      return {
        success: false,
        operation: 'cancel',
        requestId,
        error: error.message,
      };
    }
  };

  /**
   * Process a result
   */
  const processResult = (result) => {
    if (!result) return;
    
    setResults(prev => {
      const newResults = { ...prev };
      newResults.total++;
      
      if (result.success) {
        newResults.successful++;
        logMessage(`✅ ${result.operation} ${result.requestId}: Success (${result.duration?.toFixed(0)}ms)`, 'success');
      } else if (isRaceCondition(result.errorCode)) {
        newResults.raceConditions++;
        logMessage(`🔄 ${result.operation} ${result.requestId}: ${result.errorCode}`, 'warning');
      } else {
        newResults.failed++;
        newResults.errors.push(result);
        logMessage(`❌ ${result.operation} ${result.requestId}: ${result.error || result.message}`, 'error');
      }
      
      return newResults;
    });
  };

  /**
   * Check if error is a race condition
   */
  const isRaceCondition = (errorCode) => {
    const raceConditionCodes = [
      'SLOT_NO_LONGER_AVAILABLE',
      'ALREADY_RESERVED_BY_ANOTHER_STUDENT',
      'BOOKING_BEING_PROCESSED',
      'CONFIRMATION_IN_PROGRESS',
      'ALREADY_CONFIRMED',
      'PAYMENT_IN_PROGRESS',
      'STALE_DATA',
      'BOOKING_STATE_CHANGED',
      'VERSION_MISMATCH',
      'OVERLAPPING_PARTIAL_RESERVATION',
    ];
    
    return raceConditionCodes.includes(errorCode);
  };

  /**
   * Log a message
   */
  const logMessage = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setResults(prev => ({
      ...prev,
      log: [...prev.log, { timestamp, message, type }],
    }));
  };

  /**
   * Display summary
   */
  const displaySummary = () => {
    const elapsed = Date.now() - startTime.current;
    const rps = (results.total / (elapsed / 1000)).toFixed(2);
    
    logMessage('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'info');
    logMessage(`📊 Test Summary:`, 'info');
    logMessage(`Total Requests: ${results.total}`, 'info');
    logMessage(`✅ Successful: ${results.successful}`, 'success');
    logMessage(`🔄 Race Conditions Handled: ${results.raceConditions}`, 'warning');
    logMessage(`❌ Failed: ${results.failed}`, 'error');
    logMessage(`⚡ Requests/second: ${rps}`, 'info');
    logMessage(`⏱️ Duration: ${(elapsed / 1000).toFixed(2)}s`, 'info');
    
    if (results.raceConditions > 0 && results.failed === 0) {
      logMessage('✅ PASS: System correctly handled all race conditions!', 'success');
    } else if (results.failed > 0) {
      logMessage('⚠️ WARNING: Some requests failed unexpectedly', 'error');
    }
  };

  /**
   * Helper: delay function
   */
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Show unauthorized message if not admin
  if (!isAuthorized) {
    return (
      <div className="stress-test-tool">
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: '#f8f9fa',
          borderRadius: '12px',
          margin: '2rem auto',
          maxWidth: '600px'
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>
            🔒 Access Restricted
          </h2>
          <p style={{ color: '#6c757d', fontSize: '1.1rem' }}>
            This tool is restricted to administrators only.
          </p>
          <p style={{ color: '#6c757d', marginTop: '1rem' }}>
            Current user: {auth.currentUser?.email || 'Not logged in'}
          </p>
          <button 
            onClick={() => window.location.href = '/dashboard'}
            style={{
              marginTop: '2rem',
              padding: '0.75rem 2rem',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stress-test-tool">
      <h2>🧪 Booking System Stress Test Tool</h2>
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '6px',
        padding: '1rem',
        marginBottom: '1rem',
        color: '#856404'
      }}>
        <strong>⚠️ TEMPORARY TESTING TOOL</strong>
        <br />
        This tool is for testing purposes only and will be removed before final production.
        <br />
        Access restricted to: brightbridgetutors@gmail.com
      </div>
      
      <div className="test-config">
        <h3>Test Configuration</h3>
        
        <div className="config-group">
          <label>
            Booking ID to Test:
            <input
              type="text"
              value={testConfig.bookingId}
              onChange={(e) => setTestConfig({...testConfig, bookingId: e.target.value})}
              placeholder="Enter booking ID"
              disabled={isRunning}
            />
          </label>
        </div>
        
        <div className="config-group">
          <label>
            Test Type:
            <select
              value={testConfig.testType}
              onChange={(e) => setTestConfig({...testConfig, testType: e.target.value})}
              disabled={isRunning}
            >
              <option value="reservation">Concurrent Reservations</option>
              <option value="confirmation">Concurrent Confirmations</option>
              <option value="update">Rapid Updates</option>
              <option value="mixed">Mixed Operations</option>
            </select>
          </label>
        </div>
        
        <div className="config-group">
          <label>
            Concurrent Users:
            <input
              type="number"
              min="1"
              max="50"
              value={testConfig.concurrentUsers}
              onChange={(e) => setTestConfig({...testConfig, concurrentUsers: parseInt(e.target.value)})}
              disabled={isRunning}
            />
          </label>
        </div>
        
        <div className="config-group">
          <label>
            Delay Between Batches (ms):
            <input
              type="number"
              min="0"
              max="5000"
              value={testConfig.delayMs}
              onChange={(e) => setTestConfig({...testConfig, delayMs: parseInt(e.target.value)})}
              disabled={isRunning}
            />
          </label>
        </div>
        
        <div className="config-group">
          <label>
            Test Duration (ms):
            <input
              type="number"
              min="1000"
              max="60000"
              value={testConfig.duration}
              onChange={(e) => setTestConfig({...testConfig, duration: parseInt(e.target.value)})}
              disabled={isRunning}
            />
          </label>
        </div>
        
        <div className="test-controls">
          {!isRunning ? (
            <button onClick={startTest} className="btn-start">
              🚀 Start Test
            </button>
          ) : (
            <button onClick={stopTest} className="btn-stop">
              ⛔ Stop Test
            </button>
          )}
        </div>
      </div>
      
      <div className="test-results">
        <h3>Results</h3>
        
        <div className="results-summary">
          <div className="result-item">
            <span>Total:</span>
            <span>{results.total}</span>
          </div>
          <div className="result-item success">
            <span>Successful:</span>
            <span>{results.successful}</span>
          </div>
          <div className="result-item warning">
            <span>Race Conditions:</span>
            <span>{results.raceConditions}</span>
          </div>
          <div className="result-item error">
            <span>Failed:</span>
            <span>{results.failed}</span>
          </div>
        </div>
        
        <div className="test-log">
          <h4>Log Output</h4>
          <div className="log-container">
            {results.log.map((entry, index) => (
              <div key={index} className={`log-entry log-${entry.type}`}>
                <span className="log-time">{entry.timestamp}</span>
                <span className="log-message">{entry.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StressTestTool;